/**
 * Real-time event spine — wire format.
 *
 * Single source of truth for subject kinds, event types, and per-event
 * payload shapes. Governed by the invariants in
 * `apps/api/src/events/AGENTS.md` — particularly:
 *
 *   - Rule 2: no top-level payload property named `event` / `retry` / `data`,
 *             and no top-level `id` other than the envelope's outbox row id.
 *   - Rule 8: SubjectKind and ThreadEventType are closed z.enum(); Subject and
 *             ThreadEvent are Zod v4 discriminated unions. Adding a new event
 *             type happens here first; consumers' switches then fail to
 *             compile until they handle the new variant.
 *
 * Design context: docs/plans/2026-05-16-realtime-event-spine-design.md
 */

import { z } from 'zod';

import { entityEdgeKindSchema, entityKindSchema } from '../entities/kinds.ts';
import { threadCommentSchema } from '../index.ts';

// ── Subjects ─────────────────────────────────────────────────────────

// `public` is the platform-wide discovery bucket: every event that should
// surface in the public Activity feed carries the sentinel subject
// `public:all` (see `PUBLIC_SUBJECT_ID`). Anyone may subscribe to it — see
// `canSubscribe`'s `public` case. Kept in the closed enum (Rule 8) so the
// transport parser and every switch over the kind stay exhaustive.
export const subjectKindSchema = z.enum(['thread', 'org', 'user', 'public']);
export type SubjectKind = z.infer<typeof subjectKindSchema>;

export const subjectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('thread'), id: z.string().min(1) }),
  z.object({ kind: z.literal('org'),    id: z.string().min(1) }),
  z.object({ kind: z.literal('user'),   id: z.string().min(1) }),
  z.object({ kind: z.literal('public'), id: z.string().min(1) }),
]);
export type Subject = z.infer<typeof subjectSchema>;

/**
 * The single id used with the `public` subject kind. There is exactly one
 * public bucket; events tagged `public:all` surface in the platform-wide
 * Activity feed. Wire-string construction stays at the boundary
 * (`serializeSubject`, Rule 4) — callers pass `{ kind: 'public', id:
 * PUBLIC_SUBJECT_ID }`.
 */
export const PUBLIC_SUBJECT_ID = 'all';

// ── Event type enum (closed) ─────────────────────────────────────────

export const threadEventTypeSchema = z.enum([
  'thread.created',
  'thread.listable',
  'thread.completed',
  'thread.block.appended',
  'thread.comment.created',
  'thread.comment.updated',
  'thread.comment.deleted',
  // Multiplayer threads — a human joined or left a thread's participant set.
  // Thread-scoped (subject: thread:<id>), plus the affected user's own subject
  // (user:<id>) so an invitee is notified on their personal channel.
  'thread.participant.joined',
  'thread.participant.left',
  // Social events — not thread-scoped. Surface in the platform-wide and
  // per-profile Activity feeds (subjects: public + the involved users).
  'user.followed',
  'user.joined',
  // Presence pulse — the actor is online in the product as of `created_at`.
  // Org-scoped (subject: the actor's workspace org). Offline is derived by
  // the client when no pulse arrives within the online window; there is no
  // server-emitted offline event in v1.
  'user.presence',
  // LLM-wiki entity lifecycle (TAN-4134). Org-scoped; never `public:all`
  // (substrate invariant #6). Payloads are kind-generic — per-kind role
  // validation lives substrate-side (invariant #5).
  'entity.created',
  'entity.updated',
  'entity.archived',
  'entity.thread_attached',
  'entity.thread_detached',
  'entity.linked',
  'entity.superseded',
  'skill.published',
  'skill.version.accepted',
  'skill.version.proposed',
  'skill.version.rejected',
  'skill.installation.deleted',
]);
export type ThreadEventType = z.infer<typeof threadEventTypeSchema>;

// ── Envelope ─────────────────────────────────────────────────────────
//
// Fields on the envelope are shared across every variant. `id` here is the
// outbox row id (Rule 2's allowed exception). Payloads must not reintroduce a
// top-level `id`, `event`, `retry`, or `data`.

const threadEventBase = z.object({
  // Outbox row id, serialized as a string to survive JSON's 53-bit integer
  // limit. Monotonic; clients use it as the Last-Event-ID / since= cursor.
  id: z.string().min(1),
  subjects: z.array(z.string().min(1)).min(1),
  actor_user_id: z.string().min(1).nullable(),
  created_at: z.string().datetime(),
});

// ── Per-event payloads ───────────────────────────────────────────────

const threadSummarySchema = z.object({
  thread_id: z.string().min(1),
  title: z.string(),
  author_id: z.string().min(1),
  created_at: z.string().datetime(),
});

const threadBlockKindSchema = z.enum([
  'user',
  'assistant',
  'tool',
  'progress',
  'system',
]);
export type ThreadBlockKind = z.infer<typeof threadBlockKindSchema>;

const skillRootKindSchema = z.enum([
  'claude_global',
  'agents_global',
  'claude_project',
  'agents_project',
]);
const skillVisibilitySchema = z.enum(['private', 'workspace', 'public']);

const skillVersionPayloadSchema = z.object({
  skill_id: z.string().min(1),
  name: z.string().min(1),
  root_kind: skillRootKindSchema,
  version: z.number().int().positive(),
  version_id: z.string().min(1).optional(),
  package_id: z.string().min(1).optional(),
  content_hash: z.string().startsWith('md5:'),
});

// ── Discriminated union over `type` ──────────────────────────────────

export const threadEventSchema = z.discriminatedUnion('type', [
  threadEventBase.extend({
    type: z.literal('thread.created'),
    payload: threadSummarySchema,
  }),
  threadEventBase.extend({
    type: z.literal('thread.listable'),
    payload: z.object({
      thread_id: z.string().min(1),
      listable_at: z.string().datetime(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.completed'),
    payload: z.object({
      thread_id: z.string().min(1),
      completed_at: z.string().datetime(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.block.appended'),
    payload: z.object({
      thread_id: z.string().min(1),
      block_id: z.string().min(1),
      block_type: threadBlockKindSchema,
      // 140-char excerpt computed at publish time. Full block content is not
      // embedded — see design doc §"Data model" payload table.
      excerpt: z.string().nullable(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.comment.created'),
    // z.lazy() defers the binding lookup past module-load to sidestep the
    // circular import (index.ts re-exports this file).
    payload: z.object({ comment: z.lazy(() => threadCommentSchema) }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.comment.updated'),
    // z.lazy() defers the binding lookup past module-load to sidestep the
    // circular import (index.ts re-exports this file).
    payload: z.object({ comment: z.lazy(() => threadCommentSchema) }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.comment.deleted'),
    payload: z.object({
      thread_id: z.string().min(1),
      comment_id: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.participant.joined'),
    payload: z.object({
      thread_id: z.string().min(1),
      participant_user_id: z.string().min(1),
      role: z.enum(['owner', 'member']),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('thread.participant.left'),
    payload: z.object({
      thread_id: z.string().min(1),
      participant_user_id: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('user.followed'),
    // The follower is the envelope `actor_user_id` (resolved to display
    // info by event consumers that join user metadata). The followee is embedded
    // here because consumers generally enrich only the actor — no nested
    // `id` key (Rule 2), so the followee id is `followee_id`.
    payload: z.object({
      followee_id: z.string().min(1),
      followee_handle: z.string().nullable(),
      followee_display_name: z.string().nullable(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('user.joined'),
    // The joining user is the envelope `actor_user_id`. Org context is
    // embedded for the card ("joined <org>") and is null for consumer
    // signups with no workspace.
    payload: z.object({
      organization_id: z.string().min(1).nullable(),
      organization_name: z.string().nullable(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('user.presence'),
    // The present user is the envelope `actor_user_id`; the pulse time is the
    // envelope `created_at`. `status` is always 'online' in v1 — clients age a
    // user out of the online set when no pulse arrives within the window.
    payload: z.object({
      status: z.literal('online'),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.created'),
    payload: z.object({
      entity_id: z.string().min(1),
      kind: entityKindSchema,
      slug: z.string().min(1),
      name: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.updated'),
    payload: z.object({
      entity_id: z.string().min(1),
      kind: entityKindSchema,
      changed_fields: z.array(z.string()).min(1),
      actor: z.enum(['agent', 'human']),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.archived'),
    payload: z.object({
      entity_id: z.string().min(1),
      kind: entityKindSchema,
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.thread_attached'),
    // `role` is a plain string on the wire — the event payload is
    // kind-generic; per-kind role validation lives substrate-side
    // (invariant #5).
    payload: z.object({
      entity_id: z.string().min(1),
      kind: entityKindSchema,
      thread_id: z.string().min(1),
      role: z.string().min(1),
      score: z.number().nullable(),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.thread_detached'),
    payload: z.object({
      entity_id: z.string().min(1),
      kind: entityKindSchema,
      thread_id: z.string().min(1),
      role: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.linked'),
    payload: z.object({
      from_id: z.string().min(1),
      to_id: z.string().min(1),
      edge_kind: entityEdgeKindSchema,
    }),
  }),
  threadEventBase.extend({
    type: z.literal('entity.superseded'),
    payload: z.object({
      superseding_id: z.string().min(1),
      supersedes_id: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('skill.published'),
    payload: skillVersionPayloadSchema.extend({
      visibility: skillVisibilitySchema,
    }),
  }),
  threadEventBase.extend({
    type: z.literal('skill.version.accepted'),
    payload: skillVersionPayloadSchema,
  }),
  threadEventBase.extend({
    type: z.literal('skill.version.proposed'),
    payload: skillVersionPayloadSchema.extend({
      owner_user_id: z.string().min(1),
      proposer_user_id: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('skill.version.rejected'),
    payload: z.object({
      skill_id: z.string().min(1),
      version: z.number().int().positive(),
      version_id: z.string().min(1).optional(),
      package_id: z.string().min(1).optional(),
      reviewer_user_id: z.string().min(1),
      proposer_user_id: z.string().min(1),
    }),
  }),
  threadEventBase.extend({
    type: z.literal('skill.installation.deleted'),
    payload: z.object({
      skill_id: z.string().min(1),
      installation_id: z.string().min(1),
      user_id: z.string().min(1),
    }),
  }),
]);
export type ThreadEvent = z.infer<typeof threadEventSchema>;

/**
 * Property names that must never appear as top-level payload properties.
 * Re-exported for the contract test in `apps/api/src/events/contracts.test.ts`
 * so the banned set lives in one place.
 *
 * `id`, `event`, `retry`, `data` collide with SSE framing.
 */
export const RESERVED_PAYLOAD_KEYS: ReadonlySet<string> = new Set([
  'id',
  'event',
  'retry',
  'data',
]);
